import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import customVerificationService from '../../../services/customVerificationService';

const CustomEmailVerificationHandler: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleEmailVerification = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setError('Invalid verification link - missing token');
        setIsProcessing(false);
        return;
      }

      try {
        const response = await customVerificationService.verifyEmailToken(token);
        
        if (response.success && response.user_verified) {
          // Redirect to success page
          navigate('/email-verification-success');
        } else {
          setError(response.message || 'Verification failed');
          setIsProcessing(false);
        }
      } catch (error: any) {
        console.error('Email verification error:', error);
        setError(error.response?.data?.detail || 'Failed to verify email');
        setIsProcessing(false);
      }
    };

    handleEmailVerification();
  }, [searchParams, navigate]);

  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-gray-100 to-blue-50">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center mx-auto mb-6">
              <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
            
            <h1 className="text-2xl font-bold mb-4 font-inter">
              Verifying Your Email
            </h1>
            
            <p className="text-gray-600 mb-6">
              Please wait while we verify your email address...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-gray-100 to-blue-50">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-400 to-pink-500 flex items-center justify-center mx-auto mb-6">
              <span className="text-white text-3xl">
                <i className="fas fa-exclamation-triangle" />
              </span>
            </div>
            
            <h1 className="text-2xl font-bold mb-4 font-inter text-red-600">
              Verification Failed
            </h1>
            
            <p className="text-gray-600 mb-6">
              {error}
            </p>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800 text-sm">
                The verification link may have expired or is invalid. Please try requesting a new verification email from your account.
              </p>
            </div>
            
            <button
              onClick={() => navigate('/verify-email')}
              className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-bold text-base rounded-lg h-12 flex items-center justify-center transition-all"
            >
              Request New Verification Email
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default CustomEmailVerificationHandler;